import { useIntl } from "react-intl";
import { useCallback, useMemo } from "react";
import { Popconfirm, message } from "antd";
import {
  usePaymentTerminalsQuery,
  usePaymentTerminalDeleteMutation,
  PAYMENT_TERMINALS_QUERY,
} from "@ticketingplatform/api/dist/paymentTerminal";
import { PaymentTerminal } from "@ticketingplatform/api/dist/paymentTerminal/__generated__/PaymentTerminal";
import { pageToSkip, renderTableDateColumn } from "utils/tableUtils";
import { ColumnsType } from "antd/es/table";
import { DeleteIcon } from "assets/icons";
import { useVenueContext } from "contexts/VenueContext";
import { useTableQueryParams } from "hooks/useTableQueryParams";
import {
  getColumnTextFilterProps,
  getTextFilterInput,
} from "components/data/TableTextFilter/utils";

import { UsePaymentTerminalListReturnType } from "./types";

export const useDeletePaymentTerminal = () => {
  const intl = useIntl();
  const [mutation, result] = usePaymentTerminalDeleteMutation({
    evictQueries: [PAYMENT_TERMINALS_QUERY],
    onCompleted: () =>
      message.success(
        intl.formatMessage({
          defaultMessage: "Payment Terminal has been removed!",
        })
      ),
    onError: () =>
      message.error(
        intl.formatMessage({
          defaultMessage: "Cannot remove the Payment Terminal",
        })
      ),
  });

  const deletePaymentTerminal = useCallback(
    (id: number) => mutation({ variables: { id } }),
    [mutation]
  );

  return [deletePaymentTerminal, result] as const;
};

export const usePaymentTerminalColumns = () => {
  const intl = useIntl();
  const [deletePaymentTerminal] = useDeletePaymentTerminal();
  const [{ sortOrder, filters }] = useTableQueryParams<PaymentTerminal>();

  return useMemo<ColumnsType<PaymentTerminal>>(
    () => [
      {
        title: intl.formatMessage({
          defaultMessage: "Terminal name",
        }),
        dataIndex: "name",
        key: "name",
        sorter: {
          multiple: 1,
        },
        sortOrder: sortOrder.name,
        ...getColumnTextFilterProps("name", filters?.name),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Serial Number",
        }),
        dataIndex: "serialNumber",
        key: "serialNumber",
        sorter: {
          multiple: 2,
        },
        sortOrder: sortOrder.serialNumber,
        ...getColumnTextFilterProps("serialNumber", filters?.serialNumber),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Stripe ID",
        }),
        dataIndex: "stripeTerminalId",
        key: "stripeTerminalId",
        sorter: {
          multiple: 3,
        },
        sortOrder: sortOrder.stripeTerminalId,
        ...getColumnTextFilterProps(
          "stripeTerminalId",
          filters?.stripeTerminalId
        ),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Created",
        }),
        dataIndex: "createdAt",
        key: "createdAt",
        sorter: {
          multiple: 4,
        },
        sortOrder: sortOrder.createdAt,
        render: renderTableDateColumn(),
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Action",
        }),
        key: "action",
        render: (_, { id, name }) => (
          <Popconfirm
            title={intl.formatMessage(
              {
                defaultMessage:
                  "Are you sure you want to delete payment terminal: {name}?",
              },
              { name }
            )}
            onConfirm={() => deletePaymentTerminal(id)}
          >
            <DeleteIcon />
          </Popconfirm>
        ),
      },
    ],
    [deletePaymentTerminal, intl, sortOrder, filters]
  );
};

export const usePaymentTerminalList = (): UsePaymentTerminalListReturnType => {
  const { venue } = useVenueContext();
  const [{ page, pageSize, orderBy, filters }, setTableQueryParams] =
    useTableQueryParams<PaymentTerminal>();

  const variables = useMemo(
    () => ({
      take: pageSize,
      skip: pageToSkip(page, pageSize),
      filters: {
        ...(venue ? { venueId: { equals: venue.id } } : {}),
        ...getTextFilterInput(filters?.name, "name"),
        ...getTextFilterInput(filters?.serialNumber, "serialNumber"),
        ...getTextFilterInput(filters?.stripeTerminalId, "stripeTerminalId"),
      },
      orderBy,
    }),
    [
      filters?.name,
      filters?.serialNumber,
      filters?.stripeTerminalId,
      orderBy,
      page,
      pageSize,
      venue,
    ]
  );

  const { data, loading } = usePaymentTerminalsQuery({ variables });

  const onChange = useCallback<UsePaymentTerminalListReturnType["onChange"]>(
    ({ current, pageSize: newPageSize }, newFilters, sorter) => {
      setTableQueryParams({
        page: current,
        pageSize: newPageSize,
        sorter,
        filters: newFilters,
      });
    },
    [setTableQueryParams]
  );

  const { nodes: dataSource, count: total } = data?.paymentTerminals || {};

  return useMemo(
    () => ({
      loading,
      pagination: {
        pageSize,
        current: page,
        total,
      },
      dataSource,
      onChange,
    }),
    [dataSource, loading, onChange, page, pageSize, total]
  );
};
